import React, { useCallback, useEffect, useMemo, useRef } from "react";

import { Dimmer, Loader, Message, Modal } from "semantic-ui-react";

import ModInfo from "react-lib/apps/common/ModInfo";

import DENRoot, { DENContext } from "./DENRoot";
import { DENHostProps } from "./DENInterface";
import Diagram from "./diagram/Diagram";
import DiagnosisToday, { CARDNAME as DIAGNOSIS_TODAY_CARD } from "./DiagnosisToday";
import { checkIsEditDiagram, getLockStatus, getDENRootKey } from "./DENUtils";
import ReExamination from "./ReExamination";

type CardDentalExTxProps = DENHostProps & { scrollable?: boolean };

// Ex. & Tx. Created
const CardDentalExTx = (props: CardDentalExTxProps) => (
  <DENRoot key={getDENRootKey(props)} {...props} className="diagnosis-today-den" onLoaded={handleLoaded}>
    {(den: DENContext) => <DentalExTx den={den} />}
  </DENRoot>
);

const handleLoaded = (den: DENContext) => {
  const { state, onEvent, setState } = den;

  if (!state.encounterId) {
    setState({ clinicalFindingList: [], filterClinicalFindingIds: [] });

    return;
  }

  onEvent({ message: "GetSuperToothList", params: { encounter: state.encounterId } });
  onEvent({ message: "GetEvaluationFormList", params: { encounter: state.encounterInfo.id } });

  if (!state.isDataSecret) {
    onEvent({ message: "GetExamination", params: { encounter: state.encounterInfo.id, patient: state.patientDetail.id } });
    onEvent({ message: "GetPeriodontium" });
  }
};

const DentalExTx = ({ den }: { den: DENContext }) => {
  const { state, controller, onEvent, setProp, setState } = den;

  const stateRef = useRef(state);

  stateRef.current = state;

  // Get MissingTooth, ImplantTooth
  useEffect(() => {
    if (state.filterClinicalFindingIds?.length > 0) {
      onEvent({ message: "GetSpecialToothByExaminations", params: {} });
    }
  }, [state.filterClinicalFindingIds]);

  const { isStudentLocked, isApproved } = getLockStatus(state);
  const canEdit = !isStudentLocked && !isApproved;

  const isEdit = useMemo(
    () => checkIsEditDiagram(state, controller.data),
    [
      state.clinicalFindingList,
      state.periodontiumRecord,
      state.dentalExamData,
      state.faceExamData,
      state.muscleExamData,
      state.softTissueExamData,
      state.periodontalExamData,
      controller.data?.organ,
    ]
  );

  /* ------------------------------------------------------ */

  /*                         Handler                         */

  /* ------------------------------------------------------ */
  const handleSetSuperTooth = useCallback(async (toothIdSL: string, options?: { createExam?: boolean }) => {
    const superTeeth = stateRef.current.supernumeraryteeth?.map((item: any) => item.nickName) || [];

    if (superTeeth.includes(toothIdSL)) {
      await onEvent({ message: "UnSetSuperTooth", params: toothIdSL });
    } else {
      await onEvent({ message: "SetSuperTooth", params: { tooth: toothIdSL, createExam: options?.createExam ?? true } });
    }
  }, []);

  const handleSetHideBabyTooth = useCallback(() => {
    const hideBabyTooth = stateRef.current.patientDetail?.hideBabyTooth || false;

    onEvent({ message: hideBabyTooth ? "UnsetHideBabyTooth" : "SetHideBabyTooth", params: {} });
  }, []);

  const handleNoAction = useCallback(() => {}, []);

  const handleOnFocusClinialFindingsRow = useCallback((e: any, rowIndex: number, columnIndex: number) => {
    // column 0: Location, column 1: Clinical Findings
    if (columnIndex === 0) {
      setState({ selectorMode: true });
    } else if (columnIndex === 1) {
      setState({ openClinicalFindingsMenu: true });
    }

    setState({
      clinicalFindingIndex: rowIndex,
      clinicalFindingId: stateRef.current.clinicalFindingList[rowIndex].id,
    });
  }, []);

  const handleOnOpenModalDrawing = useCallback((params: any) => {
    onEvent({
      message: "SetOpenModalDrawing",
      params: { ...params, clinicalFindingList: stateRef.current.clinicalFindingList },
    });
  }, []);

  const handleSaveClinicalFindingById = useCallback(async () => {
    await onEvent({
      message: "SaveClinicalFindingById",
      params: { id: stateRef.current.clinicalFindingId, card: DIAGNOSIS_TODAY_CARD },
    });

    setState({ clinicalFindingIndex: null });
  }, []);

  const handleFinishedEditing = useCallback((id: any) => {
    setState(
      {
        selectorMode: false,
        openClinicalFindingsMenu: false,
        clinicalFindingId: null,
        clinicalFindingIndex: null,
      },
      () => {
        onEvent({ message: "SaveClinicalFindingById", params: { id, card: DIAGNOSIS_TODAY_CARD } });
      }
    );
  }, []);

  const handleRemoveRowById = useCallback((id: any) => {
    onEvent({ message: "RemoveClinicalFindingById", params: { id } });
  }, []);

  const handleShowRowDetailById = useCallback((id: number) => {
    const clinicalFindingList = stateRef.current.clinicalFindingList || [];

    // Issue 56993 ตรวจสอบ ว่า ICD10 ว่าใส่หรือไม่
    if (!clinicalFindingList.find((item: any) => item.id === id)?.icd10_label) {
      setState({ openModICD10NotFound: true });

      return;
    }

    onEvent({ message: "GetTreatmentPlanList", params: { id } });

    const index = clinicalFindingList.findIndex((item: any) => item.id === id);

    setState({
      showClinicalFindingsDetail: true,
      clinicalFindingIndex: index === -1 ? null : index,
      clinicalFindingId: id,
    });
  }, []);

  const handleCloseClinicalFindingModal = useCallback(() => {
    setState({ showClinicalFindingsDetail: false, clinicalFindingIndex: null });
  }, []);

  const encounter = state.encounterList?.[state.encounterIndex as any] || null;

  return (
    <>
      {state.isDataSecret && <Message warning size="small" content="ผู้ป่วยถูกปกปิดข้อมูล ไม่แสดงข้อมูลการตรวจ" />}

      <div className="den-diagram">
        <Diagram
          isEditDental={isEdit.isEditDental}
          isEditMuscle={isEdit.isEditMuscle}
          isEditFace={isEdit.isEditFace}
          isEditPlaque={isEdit.isEditPlaque}
          isEditSoft={isEdit.isEditSoft}
          isEditJaw={isEdit.isEditJaw}
          activeIndexDiagram={state.activeIndexDiagram}
          selectorMode={state.selectorMode}
          hiddenEvaluation={false}
          clinicalFindings={state.clinicalFindingList}
          clinicalFindingId={state.clinicalFindingId}
          clinicalFindingIndex={state.clinicalFindingIndex}
          filterClinicalFindingIds={state.filterClinicalFindingIds}
          filterItem={state.filterItem}
          setHideBabyTooth={canEdit ? handleSetHideBabyTooth : handleNoAction}
          hideBabyTooth={
            typeof state.patientDetail?.hideBabyTooth === "boolean"
              ? state.patientDetail.hideBabyTooth
              : !state.userPermisstion?.role_PEDIATRIC_DENTISTS
          }
          setSuperTooth={canEdit ? handleSetSuperTooth : handleNoAction}
          superteeth={(state.supernumeraryteeth || []).map((item: any) => item.nickName)}
          setMissingTooth={handleNoAction}
          controller={controller}
          onEvent={onEvent}
          setProp={setProp}
          masterData={controller.data}
          sub="DiagnosisToday"
          historyReviewSubTab={state.historyReviewSubTab}
          // ----- Plaque Record
          patientDetail={state.patientDetail}
          encounterInfo={state.encounterInfo}
          missingteeth={state.patientDetail?.missingteeth || []}
          partialmissingteeth={state.patientDetail?.partialmissingteeth}
          implantteeth={state.patientDetail?.implantteeth || []}
          periodontiumHistoryList={state.periodontiumHistoryList}
          periodontalExamData={state.periodontalExamData}
          periodontiumRecord={state.periodontiumRecord}
          // ----- End Plaque Record
          encounter={encounter}
          encounterIndex={state.encounterIndex}
          showDrawingChecked={state.showDrawingChecked}
          dentalCariesRisk={state.dentalCariesRisk}
          activeTabDentalCaries={state.activeTabDentalCaries}
          openModalAssessmentResults={state.openModalAssessmentResults}
          openModAssessment={state.openModAssessment}
          assessmentResults={state.assessmentResults}
          // Exam
          muscleExamData={state.muscleExamData}
          softTissueExamData={state.softTissueExamData}
          faceExamData={state.faceExamData}
          dentalExamData={state.dentalExamData}
          activeEvaluation={state.activeEvaluation}
        />
      </div>

      <div className={`diagnosis-today-dpo active-${state.activeIndexDiagram}`}>
        <DiagnosisToday
          activeIndexDiagram={state.activeIndexDiagram}
          openClinicalFindingsMenu={state.openClinicalFindingsMenu}
          controller={controller}
          medicalHistoryList={state.medicalHistoryList || []}
          // option
          statusExcludeCompleteOptions={state.statusExcludeCompleteOptions}
          statusIncludeCompleteOptions={state.statusIncludeCompleteOptions}
          urgencyOptions={state.urgencyOptions}
          dentalTreatmentList={state.dentalTreatmentList}
          // data
          selectorMode={state.selectorMode}
          handleOnFocusClinialFindingsRow={handleOnFocusClinialFindingsRow}
          handleOnOpenModalDrawing={handleOnOpenModalDrawing}
          handleSaveClinicalFindingById={handleSaveClinicalFindingById}
          onEvent={onEvent}
          chiefcomplaints={state.encounterInfo?.chiefcomplaints || ""}
          presentillness={state.encounterInfo?.presentillness || ""}
          others={state.encounterInfo?.others || ""}
          diagnosis={state.encounterInfo?.diagnosis || ""}
          prognosis={state.encounterInfo?.prognosis || ""}
          filterClinicalFindingIds={state.filterClinicalFindingIds}
          filterItem={state.filterItem}
          clinicalfindings={state.clinicalFindingList || []}
          keyboardShortcut={state.doctorProfileData?.keyboardshortcut}
          setProp={setProp}
          masterData={controller.data}
          summaryPreview={() => {}}
          onFinishedEditing={handleFinishedEditing}
          removeRowById={handleRemoveRowById}
          showRowDetailById={handleShowRowDetailById}
          showClinicalFindingsDetail={state.showClinicalFindingsDetail}
          showClinicalFindingTerms={state.showClinicalFindingTerms}
          clinicalFinding={state.clinicalFindingList?.[state.clinicalFindingIndex as any]}
          clinicalFindingIndex={state.clinicalFindingIndex}
          filteredTreatmentPlanList={state.treatmentPlanList
            ?.map((item: any, index: number) => ({ index, ...item }))
            .filter((item: any) => item.examination === state.clinicalFindingId)}
          treatmentPlanList={state.treatmentPlanList}
          clinicalFindingList={state.clinicalFindingList}
          onCloseClinicalFindingModal={handleCloseClinicalFindingModal}
          encounter={encounter}
          encounterIndex={state.encounterIndex}
          openGallery={state.openGallery}
          showDrawingChecked={state.showDrawingChecked}
          showDrawingCheckedAll={state.showDrawingCheckedAll}
          openModCancelTreatmentPlan={state.openModCancelTreatmentPlan}
          openModRemoveClinicalFinding={state.openModRemoveClinicalFinding}
          errorMessage={state.errorMessage}
          successMessage={state.successMessage}
          buttonLoadCheck={state.buttonLoadCheck}
          encounterInfo={state.encounterInfo}
          studentLocked={isStudentLocked}
          examinationTableHeight="calc(-30rem + 100vh)"
        />
      </div>

      <Modal open={!!state.openModalReExamination} onClose={() => setState({ openModalReExamination: false })}>
        <Dimmer active={!!state.isModalReExaminationLoading} inverted>
          <Loader inverted>Loading</Loader>
        </Dimmer>

        <ReExamination
          onEvent={onEvent}
          setProp={setProp}
          // data
          reExamEncounterList={state.reExamEncounterList}
          openModalFilterReExamDate={state.openModalFilterReExamDate}
          dateFilterListReExam={state.dateFilterListReExam}
          openModalFilterReExamDentist={state.openModalFilterReExamDentist}
          dentistFilterListReExam={state.dentistFilterListReExam}
          openModalFilterReExamClinic={state.openModalFilterReExamClinic}
          clinicFilterListReExam={state.clinicFilterListReExam}
        />
      </Modal>

      <ModInfo
        open={!!state.openModICD10NotFound}
        onClose={() => setState({ openModICD10NotFound: false })}
        onApprove={() => setState({ openModICD10NotFound: false })}
        closeOnDimmerClick
        alertText="กรุณาระบุ Medical Terms / ICD10"
        titleColor="blue"
        style={{ margin: "auto" }}
      />
    </>
  );
};

export default CardDentalExTx;
